/**
 * The ONE place that calls `POST /Auth/extend-session`.
 *
 * Refresh tokens rotate (single-use), so a second concurrent call — from another
 * interceptor, the scheduler, the legacy thunk path, or another tab — is not merely
 * wasteful: it spends a token that no longer exists and reads as "session over". Every
 * caller therefore funnels through `refreshSession()`, which is single-flight within the
 * tab and lease-guarded across tabs.
 *
 * Uses bare `axios`, never one of the `api-client` instances: those carry the reauth
 * interceptor, and a 401 from extend-session routed back into it would recurse.
 */

import axios from 'axios';

import { COREAPI, encodeData, getUserData, onLogout } from '@/constants/global';
import { pageurl } from '@/constants/pageurl';

import { type SessionEndedReason, writeSessionEndedNotice } from './session-ended-notice';
import { withRefreshLease } from './session-lease';

export type RefreshOutcome =
	/** We called the server and wrote a new pair. */
	| 'rotated'
	/** Storage already held a newer token than the caller saw (another tab / caller refreshed). Nothing sent. */
	| 'adopted'
	/** The server rejected the refresh token and storage still holds it: the session cannot continue. */
	| 'dead'
	/** Network / 5xx / timeout / malformed body: nothing can be concluded; do not log out. */
	| 'transient';

const EXTEND_SESSION_URL = `${COREAPI}/api/v1/Auth/extend-session`;
const REFRESH_TIMEOUT_MS = 15_000;

interface IExtendSessionBody {
	token?: string;
	refreshToken?: string;
	data?: { token?: string; refreshToken?: string } | null;
}

/**
 * The thunk path read `res.data.data.token` (Core envelope); accept a bare pair too, so
 * a backend that drops the envelope does not read as an outage.
 */
export const readExtendSessionPair = (body: IExtendSessionBody | undefined): { token: string; refreshToken: string } | null => {
	const inner = body?.data ?? body;
	const token = inner?.token;
	const refreshToken = inner?.refreshToken;
	return typeof token === 'string' && token && typeof refreshToken === 'string' && refreshToken ? { token, refreshToken } : null;
};

const isRefreshRejected = (status: number | undefined) => status === 400 || status === 401 || status === 403;

let inFlight: Promise<RefreshOutcome> | null = null;

const performRefresh = async (observedToken: string | undefined): Promise<RefreshOutcome> =>
	withRefreshLease(async () => {
		// Re-read inside the lease: whoever held it before us may have rotated already.
		const current = getUserData();
		if (!current.token) return 'dead';
		if (observedToken !== undefined && current.token !== observedToken) return 'adopted';
		if (!current.refreshToken) return 'dead';

		try {
			const res = await axios.post<IExtendSessionBody>(EXTEND_SESSION_URL, undefined, {
				timeout: REFRESH_TIMEOUT_MS,
				headers: {
					'Content-Type': 'application/json',
					'Authorization': `Bearer ${current.refreshToken}`,
				},
			});
			const pair = readExtendSessionPair(res.data);
			if (!pair) {
				console.error('[session] extend-session answered 2xx without a token pair', res.data);
				return 'transient';
			}
			encodeData(pair.token, pair.refreshToken);
			return 'rotated';
		} catch (error) {
			const status = axios.isAxiosError(error) ? error.response?.status : undefined;
			if (!isRefreshRejected(status)) return 'transient';
			// Rejected — but was it OUR token that was rejected, or one another tab already spent?
			const after = getUserData();
			return after.token && after.token !== current.token ? 'adopted' : 'dead';
		}
	});

/**
 * Refresh the session, or adopt a refresh someone else already did.
 *
 * @param observedToken the access token the caller last saw. When storage holds a
 *   different one by the time we run, the caller's problem is already solved and no
 *   request is sent. Pass `getUserData().token` at the moment you decided to refresh.
 */
export const refreshSession = (observedToken: string | undefined): Promise<RefreshOutcome> => {
	if (!inFlight) {
		inFlight = performRefresh(observedToken).finally(() => {
			inFlight = null;
		});
	}
	return inFlight;
};

let sessionEnded = false;

/**
 * Tear the session down with a reason the login page can show. Idempotent: the scheduler,
 * several interceptors and the thunk path can all conclude "dead" in the same tick.
 */
export const endSession = (reason: SessionEndedReason) => {
	if (sessionEnded) return;
	sessionEnded = true;
	writeSessionEndedNotice(reason);
	onLogout();
	window.location.href = pageurl.LOGIN;
};

/** Test seam only. */
export const __resetRefreshSessionForTests = () => {
	inFlight = null;
	sessionEnded = false;
};
